import { useAuth, useSignIn } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { CodeInput } from "@/components/auth/code-input";
import { PrimaryButton } from "@/components/auth/primary-button";
import { ResendRow } from "@/components/auth/resend-row";
import { TextLink } from "@/components/auth/text-link";
import { authPath } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { codeDigits, emptyCode } from "@/lib/code-input";
import { clearCodeSent } from "@/lib/code-sent";
import { useSendCodeOnce } from "@/lib/use-send-code-once";

const FLOW = "reset-password";

const ResetCodePage = () => {
  const navigate = useNavigate();
  const { isLoaded } = useAuth();
  const { fetchStatus, signIn } = useSignIn();
  const [code, setCode] = useState(emptyCode);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const email = signIn.identifier ?? "";
  const ready = Boolean(email);

  useEffect(() => {
    if (isLoaded && !ready) {
      navigate(authPath("/login"), { replace: true });
    }
  }, [isLoaded, navigate, ready]);

  const send = useCallback(
    () => signIn.resetPasswordEmailCode.sendCode(),
    [signIn]
  );
  useSendCodeOnce(FLOW, ready, send);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const digits = codeDigits(code);
    if (digits.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError(null);
    setPending(true);
    const { error: verifyError } =
      await signIn.resetPasswordEmailCode.verifyCode({
        code: digits,
      });
    if (verifyError) {
      setError(clerkFailureMessage(verifyError));
      setPending(false);
      return;
    }
    navigate(authPath("/login/reset/password"));
  };

  return (
    <AuthScreen
      subtitle={
        <>
          We sent a 6-digit code to{" "}
          <span className="font-medium text-navy-900">{email}</span>. Enter it
          below to choose a new password.
        </>
      }
      title="Reset your password"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <CodeInput
          error={error}
          id="reset-code"
          label="Reset code"
          onChange={(value) => {
            setCode(value);
            setError(null);
          }}
          value={code}
        />
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          Continue
        </PrimaryButton>
        <ResendRow
          alternate={
            <TextLink to={authPath("/login/password")}>
              Back to sign in
            </TextLink>
          }
          flow={FLOW}
          onResend={async () => {
            const { error: resendError } =
              await signIn.resetPasswordEmailCode.sendCode();
            if (resendError) {
              clearCodeSent(FLOW);
              setError(clerkFailureMessage(resendError));
              return;
            }
            setCode(emptyCode());
            setError(null);
          }}
        />
      </form>
    </AuthScreen>
  );
};

export default ResetCodePage;
